import React, { FC } from 'react';
import styled from 'styled-components';
import SmsIcon from '@material-ui/icons/Sms';
import EditIcon from '@material-ui/icons/Edit';
import PlaceIcon from '@material-ui/icons/Place';
import { Pagination } from '../../modules/clientModule/components/Common/Table/types';
import PaginationMobile from '../../pages/Responsive/PaginationMobile';
import { AdditionalFieldType } from './Types';

type Props = {
  headers: string[];
  rows: string[][];
  data: any[];
  additionalFields?: AdditionalFieldType[];
  Action1?: Function;
  Action2?: Function;
  currentUserId?: number;
};

const PLACEHOLDER = '—';
const renderCell = (value: any) => {
  if (value === null || value === undefined || value === '') return PLACEHOLDER;
  return value;
};

const Table20: FC<Props> = ({ headers, rows, data, additionalFields, Action1, Action2, currentUserId }) => {
  return (
    <TableCard>
      {rows.length === 0 && <NoRecords>No matching records found</NoRecords>}
      {rows.length > 0 && (
        <HeadersRow count={headers.length}>
          {headers.map((header, index) => (
            <HeaderCell key={index}>{header}</HeaderCell>
          ))}
        </HeadersRow>
      )}
      {rows.map((row, index) => {
        const fields = additionalFields[index];
        const showActions = !currentUserId || data[index]?.inspector === currentUserId;
        const projectName = fields?.[0]?.value;
        const address = fields?.[1]?.value;
        const information = fields?.[2]?.value;
        const comment = fields?.[3]?.value;
        const extra4 = fields?.[4];
        const extra5 = fields?.[5];
        return (
          <RowWrapper index={index} key={index}>
            <SummaryBanner>
              {projectName && <ProjectTitle title={projectName}>{projectName}</ProjectTitle>}
              {address && (
                <AddressLine>
                  <PlaceIcon />
                  <span>{address}</span>
                </AddressLine>
              )}
              {(information || comment || extra4 || extra5) && (
                <SecondaryLines>
                  {information && (
                    <SecondaryItem>
                      <Tag>Info</Tag>
                      <span>{information}</span>
                    </SecondaryItem>
                  )}
                  {comment && (
                    <SecondaryItem>
                      <Tag>Comment</Tag>
                      <span>{comment}</span>
                    </SecondaryItem>
                  )}
                  {extra4 && extra4.value && (
                    <SecondaryItem>
                      <Tag>{extra4.caption}</Tag>
                      <span>{extra4.value}</span>
                    </SecondaryItem>
                  )}
                  {extra5 && extra5.value && (
                    <SecondaryItem>
                      <Tag>{extra5.caption}</Tag>
                      <span>{extra5.value}</span>
                    </SecondaryItem>
                  )}
                </SecondaryLines>
              )}
            </SummaryBanner>
            <DataGrid count={headers.length}>
              {row.map((value, ndx) => (
                <DataCell key={ndx} data-label={headers[ndx]} $empty={!value && value !== 0}>
                  {renderCell(value)}
                </DataCell>
              ))}
              <ActionsCell data-label="Actions">
                {showActions && (
                  <>
                    <IconButton type="button" title="Edit" onClick={() => Action1(data[index])}>
                      <EditIcon />
                    </IconButton>
                    <IconButton type="button" title="Send SMS" onClick={() => Action2(data[index])}>
                      <SmsIcon />
                    </IconButton>
                  </>
                )}
              </ActionsCell>
            </DataGrid>
          </RowWrapper>
        );
      })}

      {rows.length > 0 && (
        <PaginationWrap>
          <Pagination>
            <PaginationMobile showEverywhere />
          </Pagination>
        </PaginationWrap>
      )}
    </TableCard>
  );
};

export default Table20;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

const TableCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 1366px) {
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    overflow: visible;
  }
`;

const PaginationWrap = styled.div`
  background: var(--neutral-0);
  border-top: 1px solid var(--border);

  @media only screen and (max-width: 1366px) {
    border-top: 0;
    background: transparent;
    margin-top: 4px;
  }

  @media print {
    display: none;
  }
`;

const HeadersRow = styled.div<{ count: number }>`
  display: grid;
  grid-template-columns: ${({ count }) => `repeat(${count}, minmax(0, 1fr))`};
  gap: 0;
  background: var(--table-row-header);
  border-bottom: 1px solid var(--border);

  @media only screen and (max-width: 1366px) {
    display: none;
  }

  @media print {
    grid-gap: 4px;
    background: #f5f5f5;
    padding: 4px 6px;
    border-bottom: 1px solid #999;
  }
`;

const HeaderCell = styled.div`
  padding: 12px 14px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
  display: flex;
  align-items: center;
  min-width: 0;

  @media print {
    padding: 0;
    font-size: 9px;
    color: #000;
    word-break: break-word;
  }
`;

const RowWrapper = styled.div<{ index }>`
  display: flex;
  flex-direction: column;
  background: ${({ index }) => (index % 2 === 1 ? 'var(--table-row-alt)' : 'var(--table-row-base)')};
  border-bottom: 1px solid var(--border);
  border-left: 3px solid transparent;
  transition: background-color 0.15s ease, border-left-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: var(--table-row-hover);
    border-left-color: var(--primary-300, #b9dcef);
  }

  @media only screen and (max-width: 1366px) {
    background: var(--neutral-0);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 14px;
    margin-bottom: 12px;
    box-shadow: var(--shadow-xs);

    &:hover {
      background: var(--neutral-0);
      border-left: 1px solid var(--border);
    }
  }

  @media print {
    background: #fff;
    border-bottom: 1px solid #999;
    border-left: 0;
    padding: 4px 0;
    page-break-inside: avoid;
  }
`;

const SummaryBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px 10px;

  @media only screen and (max-width: 1366px) {
    padding: 0 0 10px;
    border-bottom: 1px dashed var(--border);
    margin-bottom: 10px;
  }

  @media print {
    padding: 4px 6px 2px;
  }
`;

const ProjectTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media only screen and (max-width: 1366px) {
    font-size: 1rem;
    white-space: normal;
  }
`;

const AddressLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  color: var(--neutral-600);

  svg {
    font-size: 16px;
    color: var(--neutral-400);
    flex-shrink: 0;
  }

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media only screen and (max-width: 1366px) {
    > span {
      white-space: normal;
    }
  }
`;

const SecondaryLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const SecondaryItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  min-width: 0;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  @media only screen and (max-width: 1366px) {
    > span {
      white-space: normal;
    }
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--neutral-100);
  color: var(--neutral-600);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const DataGrid = styled.div<{ count: number }>`
  display: grid;
  grid-template-columns: ${({ count }) => `repeat(${count}, minmax(0, 1fr))`};
  gap: 0;
  align-items: center;
  border-top: 1px dashed var(--border);

  @media only screen and (max-width: 1366px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 6px 16px;
    border-top: 0;
  }

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  @media print {
    grid-template-columns: ${({ count }) => `repeat(${count}, 1fr)`};
    grid-gap: 4px;
    border-top: 0;
  }
`;

const DataCell = styled.div<{ $empty?: boolean }>`
  padding: 12px 14px;
  font-size: 0.875rem;
  color: ${({ $empty }) => ($empty ? 'var(--neutral-400)' : 'var(--mainText)')};
  font-family: ${FONT_STACK};
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media only screen and (max-width: 1366px) {
    padding: 4px 0;
    align-items: baseline;
    gap: 8px;
    font-size: 0.8125rem;
    white-space: normal;

    &::before {
      content: attr(data-label);
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--neutral-500);
      flex-shrink: 0;
      min-width: 96px;
    }
  }

  @media only screen and (max-width: 540px) {
    &::before {
      min-width: 84px;
    }
  }

  @media print {
    padding: 0;
    font-size: 10px;
    color: #000;
    word-break: break-word;
    white-space: normal;
    display: block;

    &::before {
      content: none;
    }
  }
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  padding: 10px 14px;

  @media only screen and (max-width: 1366px) {
    grid-column: 1 / -1;
    padding: 10px 0 0;
    margin-top: 6px;
    border-top: 1px solid var(--border);
    justify-content: flex-end;

    &::before {
      content: none;
    }
  }

  @media print {
    display: none;
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-300, #b9dcef);
    color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  @media only screen and (max-width: 1366px) {
    width: 36px;
    height: 36px;
  }
`;

const NoRecords = styled.div`
  color: var(--neutral-500);
  width: 100%;
  text-align: center;
  padding: 32px 16px;
  font-size: 0.875rem;
`;
